import React from "react";
import { Helmet } from "react-helmet-async";
import { PageMetadata, robotsToString } from "../types/seo";

interface SEOHeadProps {
  metadata: PageMetadata;
  faviconUrl?: string;
}

/**
 * SEO Head component that manages all meta tags, Open Graph, Twitter Cards, and JSON-LD
 */
export const SEOHead: React.FC<SEOHeadProps> = ({ metadata, faviconUrl }) => {
  const {
    title,
    description,
    keywords,
    canonical,
    openGraph,
    twitter,
    robots,
    noindex,
    nofollow,
    jsonLd,
  } = metadata;

  // Build robots directive
  const robotsContent = robotsToString({
    ...robots,
    index: !noindex && (robots?.index ?? true),
    follow: !nofollow && (robots?.follow ?? true),
  });

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {faviconUrl && <link rel="icon" href={faviconUrl} />}

      {/* Robots */}
      <meta name="robots" content={robotsContent} />

      {/* Open Graph / Facebook.
          Every tag is a direct child of <Helmet>: react-helmet-async walks its
          children with React.Children, which does not descend into fragments,
          so anything wrapped in <>…</> is silently dropped from the head. */}
      {openGraph && <meta property="og:type" content={openGraph.type} />}
      {openGraph && <meta property="og:title" content={openGraph.title || title} />}
      {openGraph && (
        <meta property="og:description" content={openGraph.description || description} />
      )}
      {openGraph?.image && <meta property="og:image" content={openGraph.image} />}
      {openGraph?.imageAlt && <meta property="og:image:alt" content={openGraph.imageAlt} />}
      {openGraph?.siteName && <meta property="og:site_name" content={openGraph.siteName} />}
      {openGraph?.locale && <meta property="og:locale" content={openGraph.locale} />}
      {openGraph && canonical && <meta property="og:url" content={canonical} />}

      {/* Twitter */}
      {twitter && <meta name="twitter:card" content={twitter.card} />}
      {twitter?.site && <meta name="twitter:site" content={twitter.site} />}
      {twitter?.creator && <meta name="twitter:creator" content={twitter.creator} />}
      {twitter && <meta name="twitter:title" content={twitter.title || title} />}
      {twitter && (
        <meta name="twitter:description" content={twitter.description || description} />
      )}
      {twitter?.image && <meta name="twitter:image" content={twitter.image} />}
      {twitter?.imageAlt && <meta name="twitter:image:alt" content={twitter.imageAlt} />}

      {/* JSON-LD Structured Data */}
      {jsonLd &&
        jsonLd.length > 0 &&
        jsonLd.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify({ "@context": "https://schema.org", ...schema })}
          </script>
        ))}
    </Helmet>
  );
};
